import { useEffect, useState } from 'react';

// Google OAuth configuration constants
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Debug log to check if client ID is loaded
console.log('Google Auth - Google Client ID:', GOOGLE_CLIENT_ID ? 'Loaded' : 'Missing');
// Include Calendar scope for accessing calendar events and offline access
const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'openid',
  'profile',
  'email',
  'https://www.googleapis.com/auth/calendar.events.readonly',
  // Adding offline access to get refresh tokens
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/userinfo.email'
];

// Hook to manage Google Auth state
export const useGoogleAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTimer, setRefreshTimer] = useState(null);

  // Initialize Google Auth on component mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleAuth;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      // Clear refresh timer on unmount
      if (refreshTimer) {
        if (refreshTimer.main) {
          clearTimeout(refreshTimer.main);
        }
        if (refreshTimer.monitoring) {
          refreshTimer.monitoring.forEach(timer => clearTimeout(timer));
        }
      }
    };
  }, []);

  // Schedule automatic token refresh before expiry
  const scheduleTokenRefresh = (expiresIn) => {
    // Clear any existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    
    // Schedule refresh at 45 minutes (75% of typical 60-minute token lifetime)
    // This gives us a buffer in case the refresh fails
    const refreshTime = Math.min(expiresIn * 0.75, 45 * 60) * 1000; // Convert to milliseconds
    const expiryTime = new Date(Date.now() + (expiresIn * 1000));
    
    console.log(`🔄 [Google Calendar] Token refresh scheduled:`);
    console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
    console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()} (in ${expiresIn / 60} minutes)`);
    console.log(`  - Refresh scheduled for: ${new Date(Date.now() + refreshTime).toLocaleTimeString()} (in ${refreshTime / 1000 / 60} minutes)`);
    
    // Set up monitoring intervals
    const monitoringIntervals = [];
    
    // Monitor at 15 minutes before expiry
    const fifteenMinCheck = expiresIn > 15 * 60 ? (expiresIn - 15 * 60) * 1000 : null;
    if (fifteenMinCheck && fifteenMinCheck > 0) {
      monitoringIntervals.push(setTimeout(() => {
        console.log(`⏰ [Google Calendar] 15 minutes until token expiry:`);
        console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
        console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()}`);
        console.log(`  - Refresh will occur in ${((refreshTime - fifteenMinCheck) / 1000 / 60).toFixed(1)} minutes`);
      }, fifteenMinCheck));
    }
    
    // Monitor at 5 minutes before expiry
    const fiveMinCheck = expiresIn > 5 * 60 ? (expiresIn - 5 * 60) * 1000 : null;
    if (fiveMinCheck && fiveMinCheck > 0) {
      monitoringIntervals.push(setTimeout(() => {
        console.log(`⚠️ [Google Calendar] 5 minutes until token expiry:`);
        console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
        console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()}`);
        console.log(`  - Refresh will occur in ${((refreshTime - fiveMinCheck) / 1000 / 60).toFixed(1)} minutes`);
      }, fiveMinCheck));
    }
    
    const timer = setTimeout(async () => {
      console.log(`🔄 [Google Calendar] Starting automatic token refresh...`);
      console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
      console.log(`  - Original expiry: ${expiryTime.toLocaleTimeString()}`);
      
      try {
        const newToken = await refreshToken();
        if (newToken) {
          console.log(`✅ [Google Calendar] Token refreshed successfully!`);
          console.log(`  - Refresh completed at: ${new Date().toLocaleTimeString()}`);
          console.log(`  - New token acquired, next refresh scheduled`);
        } else {
          console.error(`❌ [Google Calendar] Token refresh failed!`);
          console.error(`  - Time of failure: ${new Date().toLocaleTimeString()}`);
          console.error(`  - User will need to re-authenticate`);
        }
      } catch (error) {
        console.error(`❌ [Google Calendar] Error during token refresh:`);
        console.error(`  - Time of error: ${new Date().toLocaleTimeString()}`);
        console.error(`  - Error details:`, error);
        console.error(`  - User will need to re-authenticate`);
      }
    }, refreshTime);
    
    // Store all timers so we can clear them if needed
    setRefreshTimer({ main: timer, monitoring: monitoringIntervals });
  };
  
  // Create a function to persist authentication data for extended periods
  const storeAuthPermanently = (tokenInfo, userInfo) => {
    try {
      // Store the actual token expiry time
      const actualExpiry = new Date();
      const expiresIn = tokenInfo.expires_in || 3600; // Default to 1 hour if not provided
      actualExpiry.setSeconds(actualExpiry.getSeconds() + expiresIn);
      
      // Store the token and actual expiry
      localStorage.setItem('googleAccessToken', tokenInfo.access_token);
      localStorage.setItem('googleTokenExpiry', actualExpiry.getTime().toString());
      localStorage.setItem('googleTokenExpiresIn', expiresIn.toString());
      
      // Store other token info
      if (tokenInfo.scope) {
        localStorage.setItem('googleScope', tokenInfo.scope);
      }
      
      // Store refresh token if available
      if (tokenInfo.refresh_token) {
        localStorage.setItem('googleRefreshToken', tokenInfo.refresh_token);
      }
      
      // Store user info if available
      if (userInfo) {
        localStorage.setItem('googleUser', JSON.stringify(userInfo));
      }
      
      // Store a flag indicating we're using automatic refresh
      localStorage.setItem('googleAutoRefresh', 'true');
      
      console.log(`Google auth stored with actual expiry (${expiresIn} seconds)`);
      
      // Schedule automatic refresh
      scheduleTokenRefresh(expiresIn);
    } catch (error) {
      console.error('Error storing auth data:', error);
    }
  };
  
  const initializeGoogleAuth = async () => {
    // Check if we have stored token
    const storedToken = localStorage.getItem('googleAccessToken');
    const storedTokenExpiry = localStorage.getItem('googleTokenExpiry');
    const storedTokenExpiresIn = localStorage.getItem('googleTokenExpiresIn');
    const storedUser = localStorage.getItem('googleUser');
    const hasAutoRefresh = localStorage.getItem('googleAutoRefresh') === 'true';

    if (storedToken && storedTokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(storedTokenExpiry);
      
      if (now < expiry) {
        // Token is still valid
        console.log('Restoring Google auth session from localStorage');
        setAccessToken(storedToken);
        try {
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
        setIsAuthenticated(true);
        setIsLoading(false);
        
        // Schedule refresh for the remaining time
        const remainingTime = (expiry - now) / 1000; // Convert to seconds
        console.log(`Token expires in ${remainingTime / 60} minutes`);
        scheduleTokenRefresh(remainingTime);
      } else if (hasAutoRefresh) {
        // Token has expired but we have auto-refresh enabled
        console.log('Stored token has expired, attempting silent refresh...');
        setIsLoading(true);
        
        // Set temporary auth state to attempt refresh
        setAccessToken(storedToken);
        setIsAuthenticated(true);
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error('Error parsing stored user:', e);
          }
        }
        
        // Try to refresh the token silently
        try {
          const newToken = await refreshToken();
          if (newToken) {
            console.log('Successfully refreshed expired token on initialization');
            setIsLoading(false);
          } else {
            console.log('Failed to refresh token, clearing auth state');
            clearAuthState();
            setIsLoading(false);
          }
        } catch (error) {
          console.error('Error refreshing token on init:', error);
          clearAuthState();
          setIsLoading(false);
        }
      } else {
        // Token expired and no auto-refresh
        console.log('Google auth has expired, clearing');
        clearAuthState();
        setIsLoading(false);
      }
    } else {
      // No token exists
      setIsLoading(false);
      setIsAuthenticated(false);
      setAccessToken(null);
      setUser(null);
    }

    // Initialize Google One Tap (not using it but required for OAuth flow)
    window.google?.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
    });
    
    console.log('Google auth initialized');
  };

  const handleCredentialResponse = (response) => {
    if (response.credential) {
      // Parse the JWT token to get user info
      const payload = parseJwt(response.credential);
      
      // Set authentication state
      setAccessToken(response.credential);
      setUser({
        name: payload.name,
        email: payload.email,
        picture: payload.picture,
      });
      setIsAuthenticated(true);

      // Store in localStorage with expiry
      const expiryTime = new Date().getTime() + (payload.exp * 1000);
      localStorage.setItem('googleAccessToken', response.credential);
      localStorage.setItem('googleTokenExpiry', expiryTime.toString());
      localStorage.setItem('googleUser', JSON.stringify({
        name: payload.name,
        email: payload.email,
        picture: payload.picture,
      }));
    }
  };

  const signIn = () => {
    console.log('Starting Google OAuth flow with scopes:', SCOPES);
    
    if (window.google?.accounts.oauth2) {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPES.join(' '),
          prompt: 'consent',  // Always force consent screen
          include_granted_scopes: true, // Include previously granted scopes
          // Request offline access to get refresh tokens
          access_type: 'offline',
          callback: async (tokenResponse) => {
            if (tokenResponse.access_token) {
              const expiresIn = tokenResponse.expires_in || 3600;
              console.log(`🎉 [Google Calendar] Initial authentication successful:`);
              console.log(`  - Time: ${new Date().toLocaleTimeString()}`);
              console.log(`  - Token expires in: ${expiresIn / 60} minutes`);
              console.log(`  - Expiry time: ${new Date(Date.now() + expiresIn * 1000).toLocaleTimeString()}`);
              console.log(`  - Scopes granted: ${tokenResponse.scope}`);
              
              setAccessToken(tokenResponse.access_token);
              setIsAuthenticated(true);
              
              // Get user info first
              try {
                const userInfo = await fetchUserInfoAsync(tokenResponse.access_token);
                
                // Store with automatic refresh
                storeAuthPermanently(tokenResponse, userInfo);
                
                // Update state with user info
                setUser(userInfo);
                
                console.log(`✅ [Google Calendar] Setup complete with automatic refresh enabled`);
              } catch (error) {
                console.error('Error fetching user info:', error);
                
                // Fall back to basic storage without user info
                storeAuthPermanently(tokenResponse, null);
                console.log('Connected to Google Calendar (without user info)');
              }
            } else {
              console.error('No access token returned');
              alert('Failed to get access to your Google Calendar. Please try again.');
            }
          },
          error_callback: (error) => {
            console.error('Google OAuth error:', error);
            alert('Failed to sign in with Google: ' + (error.error || error.message || 'Unknown error'));
          }
        });
        
        // Request access token with all scopes
        tokenClient.requestAccessToken({
          prompt: 'consent',
          access_type: 'offline',
        });
      } catch (error) {
        console.error('Error initializing Google OAuth:', error);
        alert('Error setting up Google authentication. Please try again later.');
      }
    } else {
      console.error('Google OAuth2 not initialized');
      alert('Google authentication not available. Please try again later. Make sure you\'re not blocking third-party cookies.');
    }
  };
  
  // Helper function that returns a promise with user info
  const fetchUserInfoAsync = async (token) => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch user info');
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  };

  const clearAuthState = () => {
    // Clear all Google auth data from local storage
    localStorage.removeItem('googleAccessToken');
    localStorage.removeItem('googleTokenExpiry');
    localStorage.removeItem('googleTokenExpiresIn');
    localStorage.removeItem('googleUser');
    localStorage.removeItem('googleScope');
    localStorage.removeItem('googleRefreshToken');
    localStorage.removeItem('googleExtendedAuth');
    localStorage.removeItem('googleAutoRefresh');
    
    // Clear refresh timer
    if (refreshTimer) {
      if (refreshTimer.main) {
        clearTimeout(refreshTimer.main);
      }
      if (refreshTimer.monitoring) {
        refreshTimer.monitoring.forEach(timer => clearTimeout(timer));
      }
      setRefreshTimer(null);
    }
    
    // Reset state
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };
  
  const signOut = () => {
    if (window.google?.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    clearAuthState();
    
    console.log('Google account signed out');
  };

  const fetchUserInfo = async (token) => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const userInfo = await response.json();
        setUser(userInfo);
        localStorage.setItem('googleUser', JSON.stringify(userInfo));
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  // Helper to parse JWT token
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Add token refresh functionality
  const refreshToken = async () => {
    const refreshToken = localStorage.getItem('googleRefreshToken');
    
    if (!refreshToken) {
      console.error('No refresh token available, cannot refresh token');
      return null;
    }
    
    try {
      // We'll use a proxy server approach since direct client-side token refresh isn't supported
      // in standard OAuth 2.0 client implementation due to security concerns
      console.log('Attempting to silently refresh token...');
      
      // Since we can't directly refresh the token from the client side without exposing client secret,
      // we'll attempt a silent sign-in process
      if (window.google?.accounts.oauth2) {
        return new Promise((resolve, reject) => {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: SCOPES.join(' '),
            prompt: 'none',  // 'none' for silent auth - will fail if user hasn't previously consented
            include_granted_scopes: true,
            callback: (tokenResponse) => {
              if (tokenResponse.access_token) {
                const expiresIn = tokenResponse.expires_in || 3600;
                console.log(`🔄 [Google Calendar] Silent token refresh successful:`);
                console.log(`  - Time: ${new Date().toLocaleTimeString()}`);
                console.log(`  - New token expires in: ${expiresIn / 60} minutes`);
                console.log(`  - Expiry time: ${new Date(Date.now() + expiresIn * 1000).toLocaleTimeString()}`);
                
                // Set the new access token in state
                setAccessToken(tokenResponse.access_token);
                setIsAuthenticated(true);
                
                // Update localStorage with actual expiry
                const actualExpiry = new Date();
                actualExpiry.setSeconds(actualExpiry.getSeconds() + expiresIn);
                
                localStorage.setItem('googleAccessToken', tokenResponse.access_token);
                localStorage.setItem('googleTokenExpiry', actualExpiry.getTime().toString());
                localStorage.setItem('googleTokenExpiresIn', expiresIn.toString());
                
                // Schedule next refresh
                scheduleTokenRefresh(expiresIn);
                
                // Resolve with the new token
                resolve(tokenResponse.access_token);
              } else {
                console.error('Failed to refresh token silently');
                reject(new Error('Failed to refresh token'));
              }
            },
            error_callback: (error) => {
              console.error(`❌ [Google Calendar] Silent refresh failed:`, error);
              if (error.type === 'popup_failed_to_open' || error.type === 'popup_closed') {
                console.error(`  - Popup was blocked or closed`);
              } else if (error.type === 'access_denied') {
                console.error(`  - User needs to re-authenticate (access denied)`);
              } else if (error.type === 'immediate_failed') {
                console.error(`  - Silent auth not possible (user not previously authenticated)`);
              }
              console.error(`  - Error type: ${error.type}`);
              console.error(`  - Full error:`, error);
              reject(error);
            }
          });
          
          // Try to get a new access token silently
          tokenClient.requestAccessToken({
            prompt: 'none', // 'none' for truly silent auth
            hint: user?.email, // Provide user hint if available
          });
        });
      } else {
        throw new Error('Google OAuth is not initialized');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  return {
    isAuthenticated,
    accessToken,
    user,
    isLoading,
    signIn,
    signOut,
    refreshToken,
  };
};